/**
 * A clean multi-column sort utility that doesn't depend on ColumnDefinition.
 * Used for sorting data outside of TanStack tables (e.g., PurchaseOrder display).
 */

export type Comparator<T> = (a: T, b: T) => number;

type SortSpec<T> = {
  /** Function to extract the sortable value from an item */
  accessor: (item: T) => string | number;
  /** Sort direction */
  direction: 'asc' | 'desc';
  /** Optional custom compare function for complex comparisons */
  compare?: (a: T, b: T) => number;
};

/**
 * Creates a comparison function for multi-column sorting.
 *
 * @example
 * const compare = multiColumnSort<Product>([
 *   { accessor: (p) => p.department, direction: 'desc' },
 *   { accessor: (p) => p.brand, direction: 'asc' },
 *   { accessor: (p) => p.name, direction: 'asc' },
 * ]);
 * const sorted = products.sort(compare);
 */
export function multiColumnSort<T>(specs: SortSpec<T>[]) {
  return (a: T, b: T): number => {
    for (const { accessor, direction, compare } of specs) {
      let cmp: number;

      if (compare) {
        cmp = compare(a, b);
      } else {
        const aVal = accessor(a);
        const bVal = accessor(b);

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal));
        }
      }

      if (cmp !== 0) {
        return direction === 'asc' ? cmp : -cmp;
      }
    }

    return 0;
  };
}

/** Compose comparators; the first non-zero result decides the order. */
export function multiSort<T>(...comparators: Comparator<T>[]): Comparator<T> {
  return (a, b) => {
    for (const cmp of comparators) {
      const result = cmp(a, b);
      if (result !== 0) {
        return result;
      }
    }

    return 0;
  };
}

/** Compare two ordered values; nullish values sort to the end. Strings use locale-aware ordering. */
export function compare<T extends string | number | Date>(
  a: T | null | undefined,
  b: T | null | undefined
): 1 | 0 | -1 {
  if (a === b) {
    return 0;
  }

  if (a === null || a === undefined) {
    return 1;
  }

  if (b === null || b === undefined) {
    return -1;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    const cmp = a.localeCompare(b);

    return cmp === 0 ? 0 : cmp < 0 ? -1 : 1;
  }

  return a < b ? -1 : 1;
}
